// @givmo/app-admin — Settings page (07-1.4 · Payment Methods added 07-1.3 · registry EP-DES2 W0).
// The tabbed company-settings surface.
//
// A parity rebuild of the Flutter `settings` feature: Privacy Settings · Account info · Payment
// Methods (Match Settings left for its own page under Giving, `/match-program`, at EP-MNT2 — the
// route redirects `?tab=match` there). The Flutter "Payment Settings" tab lands here (07-1.3) as the
// card-entry migration off the legacy Shift4 iframe. The page loads the company ONCE
// (`companiesGetCompanyOptions` → GET /admin/company → CompanyDetailedOut) and threads it into the
// tabs.
//
// The tabs are NOT hard-coded here — they come from `settings-tab-registry.ts`, which is the shared
// -surface contract Account Security, EPSM, and White-Label register into (X5; published in
// `agent_docs/admin-settings-tabs.md`). This page's job is now purely: load the company + the
// caller's role, ask the registry which tabs are visible, and render them. Adding a tab must never
// require editing this file.
//
// The role read reuses `companiesGetCompanyAdminOptions()` — the SAME query key the app shell already
// mounts for the account region, so it is served from cache and adds no request. It exists so a
// registered tab can gate on Owner/Member without each tab re-fetching it.
//
// The root keeps `data-testid="page-settings"` — the route-guard test asserts it.

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  companiesGetCompanyAdminOptions,
  companiesGetCompanyOptions,
} from '@givmo/api-client/query';
import { isGivmoError, supportReference } from '@givmo/api-client';
import { PageHeader, Skeleton, Tabs, testIdProp } from '@givmo/design-system';
import { normalizeRole } from '../admins/admins-roles';
import {
  resolveActiveTabId,
  resolveSettingsTabs,
  settingsPanelTestId,
  settingsTabTestId,
  useSettingsTabGates,
  type SettingsTabPermissionContext,
} from './settings-tab-registry';
import { laneScrollToReveal } from './tab-lane';

const PREFIX = 'settings';

export interface SettingsPageProps {
  /**
   * The tab to open on, from the route's `?tab=` search param (AS-WEB6).
   *
   * GENERIC, and deliberately so: the page still knows nothing about which tabs exist. An unknown
   * or hidden id costs nothing because `resolveActiveTabId` already tolerates one — a stale deep
   * link resolves to the first visible tab rather than rendering empty — so this is the search
   * param plumbed into the seed the page already had, and the no-param behaviour is unchanged.
   *
   * It exists because the SSO test-connection ceremony sends the Owner's browser away to their
   * identity provider and back, and "back" has to mean the tab they left, not the first one.
   * `EmployeesPage`'s `initialTab` is the same shape — the route reads the search, the page takes a
   * prop, and the component stays renderable in a test with no router.
   */
  initialTab?: string;
}

export function SettingsPage({ initialTab }: SettingsPageProps = {}) {
  const [requestedTab, setRequestedTab] = useState<string | null>(initialTab ?? null);
  const companyQuery = useQuery(companiesGetCompanyOptions());
  const roleQuery = useQuery(companiesGetCompanyAdminOptions());
  const company = companyQuery.data;

  // Re-resolve the strip when a registrant's ASYNCHRONOUS gate settles (a tab whose dark gate is the
  // server's own answer). Not a tab registration and not tab-specific — the page still knows nothing
  // about which tabs exist. It is needed because this component can render exactly once: the role
  // comes from the query key the app shell already mounted, and the company read has a 30s
  // `staleTime`, so arriving from another page serves both from cache with no state transition to
  // re-render on, and a probe settling afterwards would otherwise reveal nothing.
  useSettingsTabGates();

  // Role stays null until the query resolves, so a gate sees "not yet known" rather than a wrong
  // default. `normalizeRole` is the same coercion the admins feature uses.
  const rawRole = roleQuery.data?.admin_company_role;
  const permissionContext: SettingsTabPermissionContext = {
    company: company ?? null,
    role: rawRole === undefined || rawRole === null ? null : normalizeRole(rawRole),
  };

  const tabs = resolveSettingsTabs(permissionContext);
  const activeTab = resolveActiveTabId(requestedTab, tabs);

  // Below `md` the strip is a sideways scroll lane (EP-WEB17), so keep the ACTIVE tab — and with it
  // the indicator — inside the lane's visible window whenever it changes. A deep-linked tab (the SSO
  // ceremony returns to `?tab=security`) otherwise renders past a phone's edge. Where the strip does
  // not scroll, every tab is already in view and this does nothing. The design system's list now
  // does the same for every strip (CBG-DS2, `revealInLane`); both land on the same offset.
  const pageRef = useRef<HTMLDivElement>(null);
  const tabIds = tabs.map((tab) => tab.id).join(' ');
  useEffect(() => {
    const lane = pageRef.current?.querySelector<HTMLElement>('[role="tablist"]');
    const active = lane?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
    if (!lane || !active) return;
    const next = laneScrollToReveal(active, lane);
    if (next !== null) lane.scrollLeft = next;
  }, [activeTab, tabIds]);

  return (
    <div
      ref={pageRef}
      className="mx-auto flex w-full max-w-[1080px] flex-col gap-20 px-24 py-24"
      {...testIdProp('page-settings')}
    >
      <PageHeader title="Settings" subtitle="Manage your company's privacy and account details." />

      <Tabs.Root
        appearance="saas"
        value={activeTab ?? undefined}
        onValueChange={(v) => setRequestedTab(String(v))}
        testId={`${PREFIX}-tabs`}
      >
        {/* Below `md`, a sideways scroll LANE, never the page (EP-WEB17): five one-line tabs need
            ~567px and a 390px phone leaves 342, so the strip wrapped its labels onto two lines and
            still pushed the document 25px wide. Each tab keeps a one-line label and the lane pans
            inside itself, with no scrollbar chrome — the statement page's pattern, including its
            tighter phone gap, so a tab cut at the lane's edge shows there is more to pan to. The
            focus ring is drawn INSIDE each tab, because the lane clips anything outside it.
            The design system's `Tabs.List` is now that lane at every width (CBG-DS2: `TAB_LANE`,
            one-line tabs, the inside focus ring, the active tab kept in view), so the `max-md:`
            classes here and the reveal effect above restate it. They stay so this page renders
            exactly as before below `md`. From `md` up every tab fits (six tabs span 707px, which
            still fits the 720px lane at 768 and again at 1024, where the sidebar returns — 13px to
            spare), so the lane never scrolls there. Being a scroll container changes only how
            Chromium antialiases the labels, by at most 1/255 per pixel (measured at CBG-DS2). */}
        <Tabs.List
          aria-label="Company settings"
          className="max-md:min-w-0 max-md:overflow-x-auto max-md:overscroll-x-contain max-md:[scrollbar-width:none] max-md:[&::-webkit-scrollbar]:hidden max-sm:gap-8"
        >
          {tabs.map((tab) => (
            <Tabs.Tab
              key={tab.id}
              value={tab.id}
              className="max-md:shrink-0 max-md:whitespace-nowrap max-md:focus-visible:outline-solid max-md:focus-visible:outline-2 max-md:focus-visible:-outline-offset-2 max-md:focus-visible:outline-brand-500"
              testId={settingsTabTestId(tab.id)}
            >
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {company === undefined ? (
          companyQuery.isError ? (
            <div
              className="flex flex-col items-start gap-4 pt-20"
              {...testIdProp(`${PREFIX}-error`)}
            >
              <p className="font-sans text-s14 font-w500 tracking-givmo text-danger-700">
                {isGivmoError(companyQuery.error)
                  ? companyQuery.error.message
                  : 'Failed to load company settings.'}
              </p>
              {isGivmoError(companyQuery.error) && supportReference(companyQuery.error) && (
                <p className="font-sans text-s12 font-w400 tracking-normal text-text-tertiary">
                  {supportReference(companyQuery.error)}
                </p>
              )}
              <button
                type="button"
                onClick={() => void companyQuery.refetch()}
                className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 hover:underline"
                {...testIdProp(`${PREFIX}-error-retry`)}
              >
                Try again
              </button>
            </div>
          ) : (
            <div className="pt-20" {...testIdProp(`${PREFIX}-loading`)}>
              <Skeleton className="h-12 w-1/3" />
              <Skeleton className="mt-12 h-12 w-full" />
              <Skeleton className="mt-12 h-12 w-2/3" />
            </div>
          )
        ) : (
          tabs.map((tab) => (
            <Tabs.Panel key={tab.id} value={tab.id} testId={settingsPanelTestId(tab.id)}>
              <tab.component company={company} role={permissionContext.role} />
            </Tabs.Panel>
          ))
        )}
      </Tabs.Root>
    </div>
  );
}
